import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Title2,
  Body1,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  DialogTrigger,
  Field,
  Input,
  Dropdown,
  Option,
  Switch,
  Textarea,
  Spinner,
  MessageBar,
  MessageBarBody,
  Badge,
  Tooltip,
} from '@fluentui/react-components';
import { AddRegular, EditRegular, DeleteRegular } from '@fluentui/react-icons';
import { apiClient } from '../api/client';
import { REGISTRY_HIVES, REGISTRY_VALUE_TYPES } from '../config/constants';

const useStyles = makeStyles({
  container: {
    margin: '0 auto',
  },
  header: {
    marginBottom: tokens.spacingVerticalXL,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalM,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: tokens.spacingHorizontalM,
  },
  table: {
    tableLayout: 'fixed' as const,
    width: '100%',
  },
  colHive: { width: '70px' },
  colKeyPath: { width: '35%' },
  colValueName: { width: '18%' },
  colType: { width: '110px' },
  colValue: { width: '15%' },
  colStatus: { width: '80px' },
  colActions: { width: '80px' },
  cellText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    marginTop: '2px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
});

interface RegistryEntry {
  _id: string;
  hive: string;
  keyPath: string;
  valueName: string;
  valueType: string;
  valueData: string | number | string[];
  description?: string;
  enabled: boolean;
}

interface RegistryListResponse {
  success: boolean;
  data: RegistryEntry[];
  count: number;
  syncVersion: number;
}

const defaultForm = {
  hive: 'HKLM',
  keyPath: '',
  valueName: '',
  valueType: 'REG_SZ',
  valueData: '' as string | number | string[],
  description: '',
  enabled: true,
};

function formatValueData(value: string | number | string[]): string {
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function RemoteRegPage() {
  const styles = useStyles();
  const [entries, setEntries] = useState<RegistryEntry[]>([]);
  const [syncVersion, setSyncVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<RegistryEntry | null>(null);
  const [form, setForm] = useState(defaultForm);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<RegistryListResponse>('/registry');
      setEntries(res.data);
      setSyncVersion(res.syncVersion);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load registry entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAdd = () => {
    setForm({ ...defaultForm });
    setEditEntry(null);
    setDialogOpen(true);
  };

  const handleEdit = (entry: RegistryEntry) => {
    setForm({
      hive: entry.hive,
      keyPath: entry.keyPath,
      valueName: entry.valueName,
      valueType: entry.valueType,
      valueData: entry.valueData,
      description: entry.description || '',
      enabled: entry.enabled,
    });
    setEditEntry(entry);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this registry entry?')) return;
    try {
      await apiClient.delete(`/registry/${id}`);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete entry');
    }
  };

  const handleSave = async () => {
    try {
      // Build the payload with correct valueData type
      let valueData: string | number | string[] = form.valueData;
      if (form.valueType === 'REG_DWORD' || form.valueType === 'REG_QWORD') {
        valueData = Number(form.valueData);
      } else if (form.valueType === 'REG_MULTI_SZ') {
        if (typeof form.valueData === 'string') {
          valueData = form.valueData.split('\n').filter(Boolean);
        }
      }

      const payload = {
        hive: form.hive,
        keyPath: form.keyPath,
        valueName: form.valueName,
        valueType: form.valueType,
        valueData,
        description: form.description || undefined,
        enabled: form.enabled,
      };

      if (editEntry) {
        await apiClient.put(`/registry/${editEntry._id}`, payload);
      } else {
        await apiClient.post('/registry', payload);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save entry');
    }
  };

  const handleTypeChange = (newType: string) => {
    // Clear valueData when type changes to avoid mismatch
    let newValueData: string | number | string[] = '';
    if (newType === 'REG_DWORD' || newType === 'REG_QWORD') {
      newValueData = 0;
    } else if (newType === 'REG_MULTI_SZ') {
      newValueData = '';
    }
    setForm({ ...form, valueType: newType, valueData: newValueData });
  };

  const renderValueInput = () => {
    switch (form.valueType) {
      case 'REG_DWORD':
      case 'REG_QWORD':
        return (
          <Field label="Value Data" required>
            <Input
              type="number"
              value={String(form.valueData)}
              onChange={(_, d) => setForm({ ...form, valueData: Number(d.value) || 0 })}
            />
          </Field>
        );
      case 'REG_MULTI_SZ':
        return (
          <Field label="Value Data (one per line)" required>
            <Textarea
              value={Array.isArray(form.valueData) ? form.valueData.join('\n') : String(form.valueData)}
              onChange={(_, d) => setForm({ ...form, valueData: d.value })}
              rows={4}
            />
          </Field>
        );
      default:
        return (
          <Field label="Value Data" required>
            <Input
              value={String(form.valueData)}
              onChange={(_, d) => setForm({ ...form, valueData: d.value })}
            />
          </Field>
        );
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Remote Registry</Title2>
        <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
          Manage Windows Registry settings applied to workstations
        </Body1>
      </div>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading ? (
        <div className={styles.loading}>
          <Spinner size="large" label="Loading..." />
        </div>
      ) : (
        <Card>
          <div className={styles.toolbar}>
            <Badge appearance="outline" size="large">
              Sync Version: {syncVersion}
            </Badge>
            <Button appearance="primary" icon={<AddRegular />} onClick={handleAdd}>
              Add Entry
            </Button>
          </div>
          <Table className={styles.table}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.colHive}>Hive</TableHeaderCell>
                <TableHeaderCell className={styles.colKeyPath}>Key Path</TableHeaderCell>
                <TableHeaderCell className={styles.colValueName}>Value Name</TableHeaderCell>
                <TableHeaderCell className={styles.colType}>Type</TableHeaderCell>
                <TableHeaderCell className={styles.colValue}>Value</TableHeaderCell>
                <TableHeaderCell className={styles.colStatus}>Status</TableHeaderCell>
                <TableHeaderCell className={styles.colActions}>Actions</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry._id}>
                  <TableCell>
                    <Badge appearance="outline">{entry.hive}</Badge>
                  </TableCell>
                  <TableCell>
                    <Tooltip content={entry.keyPath} relationship="description">
                      <span className={styles.cellText}>{entry.keyPath}</span>
                    </Tooltip>
                    {entry.description && (
                      <Tooltip content={entry.description} relationship="description">
                        <span className={styles.description}>{entry.description}</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip content={entry.valueName} relationship="description">
                      <span className={styles.cellText}>{entry.valueName}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <span className={styles.cellText}>{entry.valueType}</span>
                  </TableCell>
                  <TableCell>
                    <Tooltip content={formatValueData(entry.valueData)} relationship="description">
                      <span className={styles.cellText}>{formatValueData(entry.valueData)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Badge color={entry.enabled ? 'success' : 'danger'}>
                      {entry.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actions}>
                      <Button
                        appearance="subtle"
                        icon={<EditRegular />}
                        size="small"
                        onClick={() => handleEdit(entry)}
                      />
                      <Button
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        size="small"
                        onClick={() => handleDelete(entry._id)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div style={{ textAlign: 'center', padding: tokens.spacingVerticalXL, color: tokens.colorNeutralForeground3 }}>
                      No registry entries. Click "Add Entry" to create one.
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={(_, data) => setDialogOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{editEntry ? 'Edit Registry Entry' : 'Add Registry Entry'}</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <div className={styles.formRow}>
                  <Field label="Hive" required>
                    <Dropdown
                      value={REGISTRY_HIVES.find(h => h.key === form.hive)?.text || ''}
                      onOptionSelect={(_, d) => setForm({ ...form, hive: d.optionValue as string })}
                    >
                      {REGISTRY_HIVES.map((h) => (
                        <Option key={h.key} value={h.key} text={h.text}>{h.text}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                  <Field label="Value Type" required>
                    <Dropdown
                      value={REGISTRY_VALUE_TYPES.find(t => t.key === form.valueType)?.text || ''}
                      onOptionSelect={(_, d) => handleTypeChange(d.optionValue as string)}
                    >
                      {REGISTRY_VALUE_TYPES.map((t) => (
                        <Option key={t.key} value={t.key} text={t.text}>{t.text}</Option>
                      ))}
                    </Dropdown>
                  </Field>
                </div>
                <Field label="Key Path" required>
                  <Input
                    value={form.keyPath}
                    onChange={(_, d) => setForm({ ...form, keyPath: d.value })}
                    placeholder="SOFTWARE\MyCompany\Policy"
                  />
                </Field>
                <Field label="Value Name" required>
                  <Input
                    value={form.valueName}
                    onChange={(_, d) => setForm({ ...form, valueName: d.value })}
                    placeholder="EnableFeatureX"
                  />
                </Field>
                {renderValueInput()}
                <Field label="Description">
                  <Input
                    value={form.description}
                    onChange={(_, d) => setForm({ ...form, description: d.value })}
                    placeholder="Admin note about this entry"
                  />
                </Field>
                <Field label="Enabled">
                  <Switch
                    checked={form.enabled}
                    onChange={(_, d) => setForm({ ...form, enabled: d.checked })}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleSave}>
                Save
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
